import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  useGetMachineryDirectiveEPlan,
  useStartHighRiskCheck,
  useStartMachineCharacteristicsExtraction,
  useMachineCharacteristicsExtractionWorkflowLatest,
  useStartStandardApplicabilityDiscovery,
  useStandardApplicabilityDiscoveryWorkflowLatest,
  useStartTypeCStandardApplicabilityCheck,
  WorkflowEventType,
  type MachineryDirectiveCompliance,
  type StandardApplicability,
  type WorkflowEvent,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import type {
  OnboardingStep,
  HighRiskCheckDisplay,
} from '../components/machineryDirectiveOnboardingTypes'
import {
  getMachineryDirectiveOnboardingSidebarStepId,
  getMachineryDirectiveOnboardingSidebarStepIndex,
  getNextSidebarStepId,
  getPreviousSidebarStepId,
  isSidebarStepNavigable,
  sidebarStepIdToOnboardingStep,
  type HighRiskStepNavigationContext,
  type MachineryDirectiveOnboardingSidebarStepId,
} from '../components/machineryDirectiveOnboardingSteps'
import {
  deriveFurthestOnboardingStep,
  deriveFurthestProgressSidebarStepId,
} from '../utils/deriveFurthestOnboardingStep'
import {
  deriveMachineCharacteristicsExtractionRecovery,
  getInitialMachineCharacteristicsExtractionTaskState,
  reduceMachineCharacteristicsExtractionTaskState,
  type MachineCharacteristicsExtractionTaskState,
} from '../utils/machineCharacteristicsExtractionTaskState'
import {
  deriveStandardApplicabilityDiscoveryRecovery,
  getInitialStandardApplicabilityDiscoveryTaskState,
  reduceStandardApplicabilityDiscoveryTaskState,
  type StandardApplicabilityDiscoveryTaskState,
} from '../utils/standardApplicabilityDiscoveryTaskState'
import { isIso12100Reference, isTypeCStandard } from '../utils/standardKinds'
import {
  getInitialTypeCStandardCheckTaskState,
  reduceTypeCStandardCheckTaskState,
  type TypeCStandardCheckTaskState,
} from '../utils/typeCStandardCheckTaskState'

export type UseMachineryDirectiveComplianceOnboardingParams = {
  machineryDirectiveComplianceId: string
  backHref: string
  compliance: MachineryDirectiveCompliance | undefined
  standards: StandardApplicability[]
}

export type UseMachineryDirectiveComplianceOnboardingResult = {
  onboardingStep: OnboardingStep
  furthestProgressSidebarStepId: MachineryDirectiveOnboardingSidebarStepId
  highRiskCheck: HighRiskCheckDisplay | null
  highRiskUserOverrideReason: string | null
  userElevatedHighRiskLocked: boolean
  hasNoStandards: boolean
  allStandardsDecided: boolean
  atLeastOneAccepted: boolean
  allDecidedButNoneAccepted: boolean
  isHighRiskLocked: boolean
  canEnterFullStudio: boolean
  showOnboarding: boolean
  isTypeCReviewCompleted: boolean
  standardsDiscoveryCompleted: boolean
  workflowResultIsHighRisk: boolean | null
  isHighRiskWorkflowRunning: boolean
  highRiskWorkflowError: string | null
  isStandardsWorkflowRunning: boolean
  standardsWorkflowError: string | null
  standardsDiscoveryTaskState: StandardApplicabilityDiscoveryTaskState
  isTypeCStandardCheckRunning: boolean
  typeCStandardCheckError: string | null
  typeCStandardCheckTaskState: TypeCStandardCheckTaskState
  machineCharacteristicsExtractionCompleted: boolean
  isMachineCharacteristicsExtractionBegun: boolean
  isMachineCharacteristicsExtractionRunning: boolean
  machineCharacteristicsExtractionError: string | null
  machineCharacteristicsExtractionTaskState: MachineCharacteristicsExtractionTaskState
  isBackDisabled: boolean
  onBack: () => void
  onNavigateToSidebarStep: (
    stepId: MachineryDirectiveOnboardingSidebarStepId
  ) => void
  onHighRiskClassificationUpdated: (
    updatedCompliance?: MachineryDirectiveCompliance
  ) => void
  onBeginMachineCharacteristicsExtraction: () => void
  onContinueFromIntro: () => void
  onContinueWithEPlan: () => void
  onContinueWithoutEPlan: () => void
  isTypeBGenerateBlocked: boolean
  onBeginHighRisk: () => void
  onCloseHighRisk: () => void
  onContinueFromHighRiskResult: () => void
  onContinueFromMachineCompletion: () => void
  onContinueFromMachineLocations: () => void
  onBeginStandards: () => void
  onContinueFromStandardsReview: () => void
  onRerunTypeCStandardCheck: () => void
  onCancelStandardsDiscovery: () => void
  onCancelTypeCStandardCheck: () => void
}

export function useMachineryDirectiveComplianceOnboarding({
  machineryDirectiveComplianceId,
  backHref,
  compliance,
  standards,
}: UseMachineryDirectiveComplianceOnboardingParams): UseMachineryDirectiveComplianceOnboardingResult {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useTranslation('machineryDirectiveCompliance')
  const [onboardingStep, setOnboardingStep] = useState<OnboardingStep>('intro')

  const { data: ePlanData } = useGetMachineryDirectiveEPlan(
    machineryDirectiveComplianceId
  )
  const hasAssignedEPlan = ePlanData?.productSource != null
  const isEPlanReadyForTypeB =
    ePlanData?.productSource?.ingestionStatus === 'COMPLETED'
  const isTypeBGenerateBlocked = hasAssignedEPlan && !isEPlanReadyForTypeB

  const [isHighRiskWorkflowRunning, setIsHighRiskWorkflowRunning] =
    useState(false)
  const [highRiskWorkflowError, setHighRiskWorkflowError] = useState<
    string | null
  >(null)
  const [isStandardsWorkflowRunning, setIsStandardsWorkflowRunning] =
    useState(false)
  const [standardsWorkflowError, setStandardsWorkflowError] = useState<
    string | null
  >(null)
  const [workflowResultIsHighRisk, setWorkflowResultIsHighRisk] = useState<
    boolean | null
  >(null)
  const [lastHighRiskResultFromWorkflow, setLastHighRiskResultFromWorkflow] =
    useState<HighRiskCheckDisplay | null>(null)
  const [standardsDiscoveryCompleted, setStandardsDiscoveryCompleted] =
    useState(false)
  const [standardsDiscoveryTaskState, setStandardsDiscoveryTaskState] =
    useState<StandardApplicabilityDiscoveryTaskState>(
      getInitialStandardApplicabilityDiscoveryTaskState
    )
  const [typeCReviewCompleted, setTypeCReviewCompleted] = useState(false)
  const [isTypeCStandardCheckRunning, setIsTypeCStandardCheckRunning] =
    useState(false)
  const [typeCStandardCheckError, setTypeCStandardCheckError] = useState<
    string | null
  >(null)
  const [typeCStandardCheckTaskState, setTypeCStandardCheckTaskState] =
    useState<TypeCStandardCheckTaskState>(getInitialTypeCStandardCheckTaskState)
  const [
    isMachineCharacteristicsExtractionRunning,
    setIsMachineCharacteristicsExtractionRunning,
  ] = useState(false)
  const [
    machineCharacteristicsExtractionError,
    setMachineCharacteristicsExtractionError,
  ] = useState<string | null>(null)
  const [
    machineCharacteristicsExtractionTaskState,
    setMachineCharacteristicsExtractionTaskState,
  ] = useState<MachineCharacteristicsExtractionTaskState>(
    getInitialMachineCharacteristicsExtractionTaskState
  )
  const [
    isMachineCharacteristicsExtractionBegun,
    setIsMachineCharacteristicsExtractionBegun,
  ] = useState(false)
  const hydratedComplianceIdRef = useRef<string | null>(null)
  const hasHydratedOnboardingStepRef = useRef(false)
  const wasHighRiskLockedRef = useRef<boolean | null>(null)

  const startHighRiskMutation = useStartHighRiskCheck(
    machineryDirectiveComplianceId
  )
  const startStandardsMutation = useStartStandardApplicabilityDiscovery(
    machineryDirectiveComplianceId
  )
  const latestDiscoveryWorkflowQuery =
    useStandardApplicabilityDiscoveryWorkflowLatest(
      machineryDirectiveComplianceId,
      !startStandardsMutation.isPending
    )
  const previousLatestDiscoveryStatusRef = useRef<string | undefined>(
    undefined
  )

  useEffect(() => {
    if (startStandardsMutation.isPending) {
      return
    }

    const recovery = deriveStandardApplicabilityDiscoveryRecovery(
      latestDiscoveryWorkflowQuery.data,
      t('onboarding.discoveryFailedWithRetry')
    )
    if (recovery == null) {
      return
    }

    setIsStandardsWorkflowRunning(recovery.isStandardsWorkflowRunning)
    setStandardsDiscoveryCompleted(recovery.standardsDiscoveryCompleted)
    setStandardsWorkflowError(recovery.standardsWorkflowError)
    setStandardsDiscoveryTaskState(recovery.standardsDiscoveryTaskState)

    const currentStatus = latestDiscoveryWorkflowQuery.data?.status
    const previousStatus = previousLatestDiscoveryStatusRef.current
    if (currentStatus === 'completed' && previousStatus !== 'completed') {
      void queryClient.invalidateQueries({
        queryKey: ['standard-applicability', machineryDirectiveComplianceId],
      })
    }
    previousLatestDiscoveryStatusRef.current = currentStatus
  }, [
    latestDiscoveryWorkflowQuery.data,
    machineryDirectiveComplianceId,
    queryClient,
    startStandardsMutation.isPending,
    t,
  ])

  const startTypeCStandardCheckMutation =
    useStartTypeCStandardApplicabilityCheck(machineryDirectiveComplianceId)
  const startMachineCharacteristicsExtractionMutation =
    useStartMachineCharacteristicsExtraction(machineryDirectiveComplianceId)
  const latestMachineCharacteristicsExtractionWorkflowQuery =
    useMachineCharacteristicsExtractionWorkflowLatest(
      machineryDirectiveComplianceId,
      !startMachineCharacteristicsExtractionMutation.isPending
    )
  const previousLatestMachineCharacteristicsExtractionStatusRef = useRef<
    string | undefined
  >(undefined)

  useEffect(() => {
    if (startMachineCharacteristicsExtractionMutation.isPending) {
      return
    }

    const recovery = deriveMachineCharacteristicsExtractionRecovery(
      latestMachineCharacteristicsExtractionWorkflowQuery.data,
      t('onboarding.machineCharacteristicsExtraction.failedWithRetry')
    )
    if (recovery == null) {
      return
    }

    setIsMachineCharacteristicsExtractionRunning(
      recovery.isMachineCharacteristicsExtractionRunning
    )
    setIsMachineCharacteristicsExtractionBegun(
      recovery.isMachineCharacteristicsExtractionBegun
    )
    setMachineCharacteristicsExtractionError(
      recovery.machineCharacteristicsExtractionError
    )
    setMachineCharacteristicsExtractionTaskState(
      recovery.machineCharacteristicsExtractionTaskState
    )

    const currentStatus =
      latestMachineCharacteristicsExtractionWorkflowQuery.data?.status
    const previousStatus =
      previousLatestMachineCharacteristicsExtractionStatusRef.current
    if (currentStatus === 'completed' && previousStatus !== 'completed') {
      void queryClient.invalidateQueries({
        queryKey: [
          'machineryDirectiveCompliance',
          machineryDirectiveComplianceId,
        ],
      })
      void queryClient.invalidateQueries({
        queryKey: ['type-b-questionnaire', machineryDirectiveComplianceId],
      })
    }
    previousLatestMachineCharacteristicsExtractionStatusRef.current =
      currentStatus
  }, [
    latestMachineCharacteristicsExtractionWorkflowQuery.data,
    machineryDirectiveComplianceId,
    queryClient,
    startMachineCharacteristicsExtractionMutation.isPending,
    t,
  ])

  const machineCharacteristicsExtractionCompleted =
    compliance?.machineCharacteristicsExtractionCompletedAt != null

  const userElevatedHighRiskLocked =
    compliance?.highRiskCheckedAt != null &&
    compliance.isHighRisk === false &&
    Boolean(compliance.highRiskUserOverrideReason?.trim())
  const automatedHighRiskLocked =
    compliance?.highRiskCheckedAt != null &&
    compliance.isHighRisk === true &&
    !compliance.highRiskUserOverrideReason?.trim()

  const highRiskCheck: HighRiskCheckDisplay | null = useMemo(() => {
    if (lastHighRiskResultFromWorkflow) {
      return lastHighRiskResultFromWorkflow
    }
    if (
      compliance?.highRiskCheckedAt != null &&
      compliance?.isHighRisk != null
    ) {
      return {
        isHighRisk: compliance.isHighRisk,
        highRiskCategory: compliance.highRiskCategory ?? null,
        highRiskReasoning: compliance.highRiskReasoning ?? null,
      }
    }
    return null
  }, [lastHighRiskResultFromWorkflow, compliance])

  const onboardingRelevantStandards = useMemo(() => {
    return standards.filter((standard) => {
      return (
        isTypeCStandard(standard.type) ||
        isIso12100Reference(standard.reference)
      )
    })
  }, [standards])
  const typeBQuestionnaireStandards = useMemo(() => {
    return standards.filter((standard) => {
      return (
        standard.source === 'QUESTIONNAIRE_RULES' &&
        standard.type.trim().toUpperCase() === 'B'
      )
    })
  }, [standards])
  const hasNoStandards = onboardingRelevantStandards.length === 0
  const allStandardsDecided =
    !hasNoStandards &&
    onboardingRelevantStandards.every(
      (standard) => standard.isAccepted !== null
    )
  const atLeastOneAccepted = onboardingRelevantStandards.some(
    (standard) => standard.isAccepted === true
  )
  const allDecidedButNoneAccepted =
    !hasNoStandards && allStandardsDecided && !atLeastOneAccepted
  const isTypeCReviewCompleted =
    typeCReviewCompleted ||
    compliance?.typeBQuestionnaireGeneratedAt != null ||
    compliance?.machineCharacteristicsExtractionCompletedAt != null
  const typeBGenerated = compliance?.typeBQuestionnaireGeneratedAt != null
  const typeBDecided =
    typeBQuestionnaireStandards.length === 0 ||
    typeBQuestionnaireStandards.every((standard) => standard.isAccepted != null)
  const typeBReviewComplete = typeBGenerated && typeBDecided
  const hasStandardsProgress =
    isTypeCReviewCompleted ||
    standards.length > 0 ||
    standardsDiscoveryCompleted

  const deriveFurthestInput = useMemo(
    () => ({
      compliance,
      hasAssignedEPlan,
      typeBReviewComplete,
      isTypeCReviewCompleted,
      hasStandardsProgress,
    }),
    [
      compliance,
      hasAssignedEPlan,
      typeBReviewComplete,
      isTypeCReviewCompleted,
      hasStandardsProgress,
    ]
  )

  const furthestProgressSidebarStepId = useMemo(
    () => deriveFurthestProgressSidebarStepId(deriveFurthestInput),
    [deriveFurthestInput]
  )

  const highRiskNavigationContext: HighRiskStepNavigationContext = useMemo(
    () => ({
      highRiskCheckedAt: compliance?.highRiskCheckedAt,
      hasInSessionHighRiskResult: lastHighRiskResultFromWorkflow != null,
      userElevatedHighRiskLocked,
      automatedHighRiskLocked,
    }),
    [
      compliance?.highRiskCheckedAt,
      lastHighRiskResultFromWorkflow,
      userElevatedHighRiskLocked,
      automatedHighRiskLocked,
    ]
  )

  useEffect(() => {
    if (compliance == null) {
      return
    }

    if (hydratedComplianceIdRef.current !== machineryDirectiveComplianceId) {
      hydratedComplianceIdRef.current = machineryDirectiveComplianceId
      hasHydratedOnboardingStepRef.current = false
      wasHighRiskLockedRef.current = null
    }

    if (hasHydratedOnboardingStepRef.current) {
      return
    }

    hasHydratedOnboardingStepRef.current = true
    setOnboardingStep(deriveFurthestOnboardingStep(deriveFurthestInput))
    wasHighRiskLockedRef.current =
      userElevatedHighRiskLocked || automatedHighRiskLocked
  }, [
    compliance,
    deriveFurthestInput,
    machineryDirectiveComplianceId,
    userElevatedHighRiskLocked,
    automatedHighRiskLocked,
  ])

  useEffect(() => {
    if (!hasHydratedOnboardingStepRef.current || compliance == null) {
      return
    }

    const isHighRiskLockedNow =
      userElevatedHighRiskLocked || automatedHighRiskLocked
    const isOnHighRiskSidebarStep =
      getMachineryDirectiveOnboardingSidebarStepId(onboardingStep) ===
      'high_risk'

    if (wasHighRiskLockedRef.current === false && isHighRiskLockedNow) {
      if (userElevatedHighRiskLocked) {
        setOnboardingStep('user_high_risk_override_locked')
      } else {
        setOnboardingStep('high_risk_result')
      }
    } else if (
      wasHighRiskLockedRef.current === true &&
      !isHighRiskLockedNow &&
      isOnHighRiskSidebarStep
    ) {
      setOnboardingStep(
        sidebarStepIdToOnboardingStep('high_risk', highRiskNavigationContext)
      )
    }
    wasHighRiskLockedRef.current = isHighRiskLockedNow
  }, [
    compliance,
    onboardingStep,
    userElevatedHighRiskLocked,
    automatedHighRiskLocked,
    highRiskNavigationContext,
  ])

  const hasHighRiskUserOverrideReason = Boolean(
    compliance?.highRiskUserOverrideReason?.trim()
  )
  const agentSaysHighRisk =
    workflowResultIsHighRisk === true ||
    (compliance?.highRiskCheckedAt != null && compliance?.isHighRisk === true)
  const isHighRiskLocked = agentSaysHighRisk && !hasHighRiskUserOverrideReason
  const canEnterFullStudio =
    !isHighRiskLocked &&
    !userElevatedHighRiskLocked &&
    !isStandardsWorkflowRunning &&
    ((hasNoStandards && standardsDiscoveryCompleted) ||
      (allStandardsDecided && atLeastOneAccepted) ||
      allDecidedButNoneAccepted) &&
    typeBReviewComplete
  const showOnboarding = isHighRiskLocked || !canEnterFullStudio

  const isBackDisabled =
    isHighRiskWorkflowRunning ||
    isStandardsWorkflowRunning ||
    isTypeCStandardCheckRunning ||
    isMachineCharacteristicsExtractionRunning

  const advanceOneSidebarStep = useCallback(() => {
    const currentSidebarId =
      getMachineryDirectiveOnboardingSidebarStepId(onboardingStep)
    const nextSidebarId = getNextSidebarStepId(currentSidebarId)
    if (nextSidebarId == null) {
      return
    }

    const highRiskSidebarIndex =
      getMachineryDirectiveOnboardingSidebarStepIndex('high_risk')
    const nextSidebarIndex =
      getMachineryDirectiveOnboardingSidebarStepIndex(nextSidebarId)
    const isLockedNow =
      userElevatedHighRiskLocked || automatedHighRiskLocked

    if (isLockedNow && nextSidebarIndex > highRiskSidebarIndex) {
      setOnboardingStep(
        sidebarStepIdToOnboardingStep('high_risk', highRiskNavigationContext)
      )
      return
    }

    setOnboardingStep(
      sidebarStepIdToOnboardingStep(nextSidebarId, highRiskNavigationContext)
    )
  }, [
    onboardingStep,
    userElevatedHighRiskLocked,
    automatedHighRiskLocked,
    highRiskNavigationContext,
  ])

  const onBack = useCallback(() => {
    if (isBackDisabled) {
      return
    }

    const currentSidebarId =
      getMachineryDirectiveOnboardingSidebarStepId(onboardingStep)
    const previousSidebarId = getPreviousSidebarStepId(currentSidebarId)
    if (previousSidebarId == null) {
      return
    }

    setOnboardingStep(
      sidebarStepIdToOnboardingStep(
        previousSidebarId,
        highRiskNavigationContext
      )
    )
  }, [isBackDisabled, onboardingStep, highRiskNavigationContext])

  const onNavigateToSidebarStep = useCallback(
    (targetStepId: MachineryDirectiveOnboardingSidebarStepId) => {
      if (isBackDisabled) {
        return
      }

      if (
        !isSidebarStepNavigable(
          targetStepId,
          furthestProgressSidebarStepId
        )
      ) {
        return
      }

      const currentSidebarId =
        getMachineryDirectiveOnboardingSidebarStepId(onboardingStep)
      if (targetStepId === currentSidebarId) {
        return
      }

      const highRiskSidebarIndex =
        getMachineryDirectiveOnboardingSidebarStepIndex('high_risk')
      const targetSidebarIndex =
        getMachineryDirectiveOnboardingSidebarStepIndex(targetStepId)
      const isLockedNow =
        userElevatedHighRiskLocked || automatedHighRiskLocked

      if (isLockedNow && targetSidebarIndex > highRiskSidebarIndex) {
        return
      }

      setOnboardingStep(
        sidebarStepIdToOnboardingStep(
          targetStepId,
          highRiskNavigationContext
        )
      )
    },
    [
      isBackDisabled,
      furthestProgressSidebarStepId,
      onboardingStep,
      userElevatedHighRiskLocked,
      automatedHighRiskLocked,
      highRiskNavigationContext,
    ]
  )

  const onHighRiskClassificationUpdated = useCallback(
    (updatedCompliance?: MachineryDirectiveCompliance) => {
      const nextCompliance = updatedCompliance ?? compliance
      const nextContext: HighRiskStepNavigationContext = {
        highRiskCheckedAt: nextCompliance?.highRiskCheckedAt,
        hasInSessionHighRiskResult: lastHighRiskResultFromWorkflow != null,
        userElevatedHighRiskLocked:
          nextCompliance?.highRiskCheckedAt != null &&
          nextCompliance.isHighRisk === false &&
          Boolean(nextCompliance.highRiskUserOverrideReason?.trim()),
        automatedHighRiskLocked:
          nextCompliance?.highRiskCheckedAt != null &&
          nextCompliance.isHighRisk === true &&
          !nextCompliance.highRiskUserOverrideReason?.trim(),
      }

      if (
        getMachineryDirectiveOnboardingSidebarStepId(onboardingStep) !==
        'high_risk'
      ) {
        return
      }

      setOnboardingStep(
        sidebarStepIdToOnboardingStep('high_risk', nextContext)
      )
    },
    [compliance, lastHighRiskResultFromWorkflow, onboardingStep]
  )

  const startMachineCharacteristicsExtraction = useCallback(() => {
    if (
      machineCharacteristicsExtractionCompleted ||
      isMachineCharacteristicsExtractionRunning
    ) {
      return
    }
    Sentry.addBreadcrumb({
      category: 'sa3.machine_characteristics_extraction',
      level: 'info',
      message: 'started',
      data: { complianceId: machineryDirectiveComplianceId },
    })
    setMachineCharacteristicsExtractionError(null)
    setMachineCharacteristicsExtractionTaskState(
      getInitialMachineCharacteristicsExtractionTaskState()
    )
    setIsMachineCharacteristicsExtractionBegun(true)
    setIsMachineCharacteristicsExtractionRunning(true)
    startMachineCharacteristicsExtractionMutation.mutate(
      {
        onEvent: (event: WorkflowEvent) => {
          setMachineCharacteristicsExtractionTaskState((previous) =>
            reduceMachineCharacteristicsExtractionTaskState(previous, event)
          )
        },
        onComplete: () => {
          Sentry.addBreadcrumb({
            category: 'sa3.machine_characteristics_extraction',
            level: 'info',
            message: 'completed',
            data: { complianceId: machineryDirectiveComplianceId },
          })
          setIsMachineCharacteristicsExtractionRunning(false)
        },
      },
      {
        onError: (error) => {
          const isAbortError =
            (error instanceof DOMException && error.name === 'AbortError') ||
            (error instanceof Error && error.name === 'AbortError')
          if (isAbortError) {
            return
          }
          Sentry.addBreadcrumb({
            category: 'sa3.machine_characteristics_extraction',
            level: 'error',
            message: 'failed',
            data: {
              complianceId: machineryDirectiveComplianceId,
              error: error.message,
            },
          })
          Sentry.captureException(error, {
            tags: {
              area: 'machinery-directive-onboarding',
              workflow: 'machine-characteristics-extraction',
            },
            extra: {
              complianceId: machineryDirectiveComplianceId,
            },
          })
          setIsMachineCharacteristicsExtractionRunning(false)
          setIsMachineCharacteristicsExtractionBegun(false)
          setMachineCharacteristicsExtractionTaskState(
            getInitialMachineCharacteristicsExtractionTaskState()
          )
          setMachineCharacteristicsExtractionError(
            t('onboarding.machineCharacteristicsExtraction.failedWithRetry')
          )
        },
      }
    )
  }, [
    isMachineCharacteristicsExtractionRunning,
    machineCharacteristicsExtractionCompleted,
    machineryDirectiveComplianceId,
    startMachineCharacteristicsExtractionMutation,
    t,
  ])

  const onBeginMachineCharacteristicsExtraction = () => {
    startMachineCharacteristicsExtraction()
  }

  const onContinueFromIntro = () => {
    const hasHighRiskResult =
      compliance?.highRiskCheckedAt != null ||
      lastHighRiskResultFromWorkflow != null
    if (hasAssignedEPlan && !hasHighRiskResult) {
      setOnboardingStep(
        sidebarStepIdToOnboardingStep('high_risk', highRiskNavigationContext)
      )
      return
    }
    setOnboardingStep('e_plan')
  }
  const onContinueWithEPlan = () => {
    setOnboardingStep(
      sidebarStepIdToOnboardingStep('high_risk', highRiskNavigationContext)
    )
  }
  const onContinueWithoutEPlan = () => {
    setOnboardingStep(
      sidebarStepIdToOnboardingStep('high_risk', highRiskNavigationContext)
    )
  }
  const onBeginHighRisk = () => {
    setHighRiskWorkflowError(null)
    setWorkflowResultIsHighRisk(null)
    setIsHighRiskWorkflowRunning(true)
    startHighRiskMutation.mutate(
      {
        onEvent: (event: WorkflowEvent) => {
          if (event.type === WorkflowEventType.WorkflowCompleted) {
            const result = event.result as
              | {
                  isHighRisk?: boolean
                  category?: string | null
                  reasoning?: string | null
                }
              | undefined
            if (typeof result?.isHighRisk === 'boolean') {
              setWorkflowResultIsHighRisk(result.isHighRisk)
              setLastHighRiskResultFromWorkflow({
                isHighRisk: result.isHighRisk,
                highRiskCategory: result.category ?? null,
                highRiskReasoning: result.reasoning ?? null,
              })
              setOnboardingStep('high_risk_result')
            }
          }
        },
        onComplete: () => setIsHighRiskWorkflowRunning(false),
      },
      {
        onError: (error) => {
          Sentry.addBreadcrumb({
            category: 'sa1.high_risk_discovery',
            level: 'error',
            message: 'failed',
            data: {
              complianceId: machineryDirectiveComplianceId,
              error: error.message,
            },
          })
          Sentry.captureException(error, {
            tags: {
              area: 'machinery-directive-onboarding',
              workflow: 'high-risk-check',
            },
            extra: {
              complianceId: machineryDirectiveComplianceId,
            },
          })
          setIsHighRiskWorkflowRunning(false)
          setHighRiskWorkflowError(t('onboarding.discoveryFailedWithRetry'))
        },
      }
    )
  }
  const onCloseHighRisk = () => navigate(backHref)
  const onContinueFromHighRiskResult = () => {
    advanceOneSidebarStep()
  }
  const onContinueFromMachineCompletion = () => {
    advanceOneSidebarStep()
  }
  const onContinueFromMachineLocations = () => {
    advanceOneSidebarStep()
  }
  const onContinueFromStandardsReview = () => {
    setTypeCReviewCompleted(true)
    setMachineCharacteristicsExtractionError(null)
    setIsMachineCharacteristicsExtractionBegun(false)
    advanceOneSidebarStep()
  }
  const onBeginStandards = () => {
    setTypeCReviewCompleted(false)
    Sentry.addBreadcrumb({
      category: 'sa1.standard_discovery',
      level: 'info',
      message: 'started',
      data: { complianceId: machineryDirectiveComplianceId },
    })
    setStandardsWorkflowError(null)
    setStandardsDiscoveryTaskState(
      getInitialStandardApplicabilityDiscoveryTaskState()
    )
    setIsStandardsWorkflowRunning(true)
    startStandardsMutation.mutate(
      {
        onEvent: (event: WorkflowEvent) => {
          setStandardsDiscoveryTaskState((previous) =>
            reduceStandardApplicabilityDiscoveryTaskState(previous, event)
          )
        },
        onComplete: () => {
          Sentry.addBreadcrumb({
            category: 'sa1.standard_discovery',
            level: 'info',
            message: 'completed',
            data: { complianceId: machineryDirectiveComplianceId },
          })
          setIsStandardsWorkflowRunning(false)
          setStandardsDiscoveryCompleted(true)
        },
      },
      {
        onError: (error) => {
          Sentry.addBreadcrumb({
            category: 'sa1.standard_discovery',
            level: 'error',
            message: 'failed',
            data: {
              complianceId: machineryDirectiveComplianceId,
              error: error.message,
            },
          })
          Sentry.captureException(error, {
            tags: {
              area: 'machinery-directive-onboarding',
              workflow: 'standard-applicability-discovery',
            },
            extra: {
              complianceId: machineryDirectiveComplianceId,
            },
          })
          setIsStandardsWorkflowRunning(false)
          setStandardsDiscoveryTaskState(
            getInitialStandardApplicabilityDiscoveryTaskState()
          )
          setStandardsWorkflowError(t('onboarding.discoveryFailedWithRetry'))
        },
      }
    )
  }

  const onRerunTypeCStandardCheck = () => {
    Sentry.addBreadcrumb({
      category: 'sa1.type_c_standard_check',
      level: 'info',
      message: 'started',
      data: { complianceId: machineryDirectiveComplianceId },
    })
    setTypeCStandardCheckError(null)
    setTypeCStandardCheckTaskState(getInitialTypeCStandardCheckTaskState())
    setIsTypeCStandardCheckRunning(true)
    startTypeCStandardCheckMutation.mutate(
      {
        onEvent: (event: WorkflowEvent) => {
          setTypeCStandardCheckTaskState((previous) =>
            reduceTypeCStandardCheckTaskState(previous, event)
          )
        },
        onComplete: () => {
          Sentry.addBreadcrumb({
            category: 'sa1.type_c_standard_check',
            level: 'info',
            message: 'completed',
            data: { complianceId: machineryDirectiveComplianceId },
          })
          setIsTypeCStandardCheckRunning(false)
        },
      },
      {
        onError: (error) => {
          Sentry.addBreadcrumb({
            category: 'sa1.type_c_standard_check',
            level: 'error',
            message: 'failed',
            data: {
              complianceId: machineryDirectiveComplianceId,
              error: error.message,
            },
          })
          Sentry.captureException(error, {
            tags: {
              area: 'machinery-directive-onboarding',
              workflow: 'type-c-standard-applicability-check-rerun',
            },
            extra: {
              complianceId: machineryDirectiveComplianceId,
            },
          })
          setIsTypeCStandardCheckRunning(false)
          setTypeCStandardCheckTaskState(
            getInitialTypeCStandardCheckTaskState()
          )
          setTypeCStandardCheckError(
            t('onboarding.typeCStandardCheckDiscovery.failedWithRetry')
          )
        },
      }
    )
  }

  const onCancelStandardsDiscovery = () => {
    void startStandardsMutation.cancel()
  }

  const onCancelTypeCStandardCheck = () => {
    void startTypeCStandardCheckMutation.cancel()
  }

  const highRiskUserOverrideReason =
    compliance?.highRiskUserOverrideReason?.trim() || null

  return {
    onboardingStep,
    furthestProgressSidebarStepId,
    highRiskCheck,
    highRiskUserOverrideReason,
    userElevatedHighRiskLocked,
    hasNoStandards,
    allStandardsDecided,
    atLeastOneAccepted,
    allDecidedButNoneAccepted,
    isHighRiskLocked,
    canEnterFullStudio,
    showOnboarding,
    isTypeCReviewCompleted,
    standardsDiscoveryCompleted,
    workflowResultIsHighRisk,
    isHighRiskWorkflowRunning,
    highRiskWorkflowError,
    isStandardsWorkflowRunning,
    standardsWorkflowError,
    standardsDiscoveryTaskState,
    isTypeCStandardCheckRunning,
    typeCStandardCheckError,
    typeCStandardCheckTaskState,
    machineCharacteristicsExtractionCompleted,
    isMachineCharacteristicsExtractionBegun,
    isMachineCharacteristicsExtractionRunning,
    machineCharacteristicsExtractionError,
    machineCharacteristicsExtractionTaskState,
    isBackDisabled,
    onBack,
    onNavigateToSidebarStep,
    onHighRiskClassificationUpdated,
    onBeginMachineCharacteristicsExtraction,
    onContinueFromIntro,
    onContinueWithEPlan,
    onContinueWithoutEPlan,
    isTypeBGenerateBlocked,
    onBeginHighRisk,
    onCloseHighRisk,
    onContinueFromHighRiskResult,
    onContinueFromMachineCompletion,
    onContinueFromMachineLocations,
    onBeginStandards,
    onContinueFromStandardsReview,
    onRerunTypeCStandardCheck,
    onCancelStandardsDiscovery,
    onCancelTypeCStandardCheck,
  }
}
